import { useState } from "react";
import { RefreshCw } from "lucide-react";
import type { Task, TaskStats, TaskTradeTotals, WalletBalanceSnapshot } from "../api/types";
import { api } from "../api/client";
import type { TaskExecutionEvent } from "../hooks/useTaskStream";
import { formatTokenAmount } from "../lib/formatTokenAmount";
import { FieldHelp } from "./FieldHelp";
import { ExecutionHistory } from "./ExecutionHistory";

export function TaskDetails({
  task,
  stats,
  tradeTotals,
  executions,
}: {
  task: Task;
  stats: TaskStats | null;
  tradeTotals: TaskTradeTotals;
  executions: TaskExecutionEvent[];
}): JSX.Element {
  const [manualBalances, setManualBalances] = useState<WalletBalanceSnapshot[] | null>(null);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const nativeUnit = task.chain === "solana" ? "SOL" : task.chain === "bnb" ? "BNB" : task.chain === "arc" ? "USDC" : "ETH";

  function fmtNum(n: number, digits = 2): string {
    return n.toLocaleString("en-US", { maximumFractionDigits: digits });
  }

  function fmtUsd(n: number | undefined): string {
    return n === undefined ? "none" : `$${fmtNum(n, 0)}`;
  }

  function shortId(id: string): string {
    return id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
  }

  // Wallets: prefer live snapshot from SSE (has balances + idle); fall back
  // to DB pubkeys when stopped — wallets stay in DB across stop/start, the
  // empty list users used to see was just the SSE stream going silent.
  const walletsFromDb = task.walletPubkeys.map((pubkey, i) => ({
    label: `W${i + 1}`,
    pubkey,
    live: false as const,
  }));
  const walletsLive = stats?.wallets.map((w) => ({ ...w, live: true as const })) ?? null;
  const walletsDisplay = walletsLive && walletsLive.length > 0 ? walletsLive : walletsFromDb;
  const manualBalanceByPubkey = new Map(
    (manualBalances ?? []).map((wallet) => [wallet.pubkey.toLowerCase(), wallet]),
  );
  const liveBadge = stats !== null;
  const maxPosition =
    task.chain === "solana"
      ? task.config.maxPositionSol
      : task.config.maxPositionEth;
  const maxExposure =
    task.chain === "solana"
      ? task.config.maxTotalExposureSol
      : task.config.maxTotalExposureEth;
  const executionRoute =
    task.config.executionPool !== undefined
      ? `pool ${shortId(task.config.executionPool)}`
      : (task.config.quoteMode ?? "global");
  const settlementLabel =
    (task.chain === "robinhood" || task.chain === "base") && task.config.settleNative
      ? "ETH via verified bridge"
      : executionRoute;

  async function refreshBalances(): Promise<void> {
    if (task.walletPubkeys.length === 0) return;
    setLoadingBalances(true);
    setBalanceError(null);
    try {
      const result = await api.post<{ wallets: WalletBalanceSnapshot[] }>("/api/wallets/balances", {
        pubkeys: task.walletPubkeys,
        chain: task.chain,
        mint: task.mint,
      });
      setManualBalances(result.wallets);
    } catch (err) {
      setBalanceError(err instanceof Error ? err.message : "Balance lookup failed");
    } finally {
      setLoadingBalances(false);
    }
  }

  return (
    <div className="bg-hover border-t border-border overflow-x-hidden">
      <div className="grid grid-cols-2 gap-6 border-b border-border px-4 py-4 text-xs sm:px-6">
        <div>
          <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
            Total bought (USD)
            <FieldHelp>Our confirmed countertrade purchases across the entire campaign and all its wallets, including previous runs. Uses the recorded USD spend at execution time, excluding fees. Pending, failed and dry-run trades are excluded.</FieldHelp>
          </div>
          <div className="font-mono text-ok text-base">${tradeTotals.buyUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className="font-mono text-[11px] text-muted mt-1">{tradeTotals.buyCount} confirmed trades</div>
        </div>
        <div>
          <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
            Total sold (USD)
            <FieldHelp>Our confirmed countertrade sales across the entire campaign and all its wallets, including previous runs. Uses recorded actual USD proceeds where available; otherwise uses the trade-size estimate at execution time (≈). Excludes fees, pending, failed and dry-run trades.</FieldHelp>
          </div>
          <div className="font-mono text-err text-base">{tradeTotals.estimatedSellCount > 0 ? "≈" : ""}${tradeTotals.sellUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className="font-mono text-[11px] text-muted mt-1">
            {tradeTotals.sellCount} confirmed trades
            {tradeTotals.estimatedSellCount > 0 && ` · ${tradeTotals.estimatedSellCount} estimated`}
          </div>
        </div>
      </div>
      {liveBadge ? (
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-4 py-4 text-xs sm:grid-cols-3 sm:px-6 xl:grid-cols-6">
          <div>
            <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
              Uptime
              <FieldHelp>How long this task has been continuously running since its latest start.</FieldHelp>
            </div>
            <div className="font-mono">{stats!.uptimeSec}s</div>
          </div>
          <div>
            <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
              Tx / Decoded
              <FieldHelp>Transactions received for this token or pool, then the subset recognised as real swaps. Non-swap activity is not counted as decoded.</FieldHelp>
            </div>
            <div className="font-mono">
              {stats!.tx} / {stats!.decoded}
            </div>
          </div>
          <div>
            <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
              Buys / Sells
              <FieldHelp>Direction of all decoded external swaps. A BUY task reacts only to sells; a SELL task reacts only to buys.</FieldHelp>
            </div>
            <div className="font-mono">
              {stats!.buys} / {stats!.sells}
            </div>
          </div>
          <div>
            <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
              FDV
              <FieldHelp>Live fully diluted value used by the market-cap range. “out” means the current value is outside this task’s configured range.</FieldHelp>
            </div>
            <div className="font-mono">
              {stats!.fdvUsd !== null ? `$${fmtNum(stats!.fdvUsd, 0)}` : "—"}
              {!stats!.mcInRange && <span className="text-warn ml-1">out</span>}
            </div>
          </div>
          <div>
            <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
              Triggers
              <FieldHelp>Green is the number of signals approved for a counter-trade. The total is every decoded swap evaluated by the strategy.</FieldHelp>
            </div>
            <div className="font-mono">
              <span className="text-ok">{stats!.triggers.approved}</span>
              <span className="text-muted"> / {stats!.triggers.evaluated}</span>
            </div>
          </div>
          <div>
            <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
              Exposure
              <FieldHelp>Net amount currently tracked by this task. For BUY tasks it is cumulative spend and is compared with Max exposure.</FieldHelp>
            </div>
            <div className="font-mono">{fmtNum(stats!.exposureSol, 4)} {nativeUnit}</div>
          </div>
          {stats!.trading && (stats!.trading.buys > 0 || stats!.trading.sells > 0 || stats!.trading.gasNative > 0) && (
            <div className="col-span-full grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
                  Spent / received
                  <FieldHelp>Native coin spent by this task's landed buys and received by its landed sells, across all runs.</FieldHelp>
                </div>
                <div className="font-mono">
                  {fmtNum(stats!.trading.spentNative, 4)} / {fmtNum(stats!.trading.receivedNative, 4)} {nativeUnit}
                </div>
              </div>
              <div>
                <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
                  Position
                  <FieldHelp>Tokens bought minus tokens sold by this task. Negative for a task that sells inventory it did not buy. The average cost covers buys whose output the receipt showed.</FieldHelp>
                </div>
                <div className="font-mono">
                  {fmtNum(stats!.trading.positionTokens, 2)}
                  {stats!.trading.avgCostNative !== null && (
                    <span className="text-muted"> @ {stats!.trading.avgCostNative.toPrecision(4)} {nativeUnit}</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
                  Realized PnL
                  <FieldHelp>Sell proceeds minus the sold tokens at the average buy cost, before gas. Shown once the task has both buys and measured sells.</FieldHelp>
                </div>
                <div className={`font-mono ${(stats!.trading.realizedPnlNative ?? 0) < 0 ? "text-warn" : ""}`}>
                  {stats!.trading.realizedPnlNative === null ? "—" : `${fmtNum(stats!.trading.realizedPnlNative, 4)} ${nativeUnit}`}
                </div>
              </div>
              <div>
                <div className="text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
                  Gas spent
                  <FieldHelp>Native gas paid by this task's swaps, including reverted ones. Approvals are not included.</FieldHelp>
                </div>
                <div className="font-mono">{fmtNum(stats!.trading.gasNative, 5)} {nativeUnit}</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="px-6 py-3 text-[11px] text-muted font-mono uppercase tracking-nav border-b border-border">
          DB snapshot — start task to see live stats
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-6 gap-y-4 border-t border-border px-4 py-4 text-xs sm:px-6 lg:grid-cols-5">
        <div>
          <div className="text-[11px] text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
            Min trigger
            <FieldHelp>Signals smaller than this USD value are ignored. “none” means there is no minimum size filter.</FieldHelp>
          </div>
          <div className="font-mono">{fmtUsd(task.config.minTriggerUsd)}</div>
        </div>
        <div>
          <div className="text-[11px] text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
            Max trigger
            <FieldHelp>Signals larger than this USD value are ignored. “none” means there is no maximum size filter.</FieldHelp>
          </div>
          <div className="font-mono">{fmtUsd(task.config.maxTriggerUsd)}</div>
        </div>
        <div>
          <div className="text-[11px] text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
            Max position
            <FieldHelp>Maximum size of one wallet leg. A larger reaction can be split across multiple wallets.</FieldHelp>
          </div>
          <div className="font-mono">
            {maxPosition === undefined ? "none" : `${fmtNum(maxPosition, 4)} ${nativeUnit}`}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
            Max exposure
            <FieldHelp>Maximum cumulative spend for this BUY task while it runs. Once reached, later signals are skipped with exposure-limit.</FieldHelp>
          </div>
          <div className="font-mono">
            {maxExposure === undefined ? "none" : `${fmtNum(maxExposure, 4)} ${nativeUnit}`}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-muted uppercase tracking-nav mb-1 flex items-center gap-1">
            Send mode
            <FieldHelp>Shows the selected sending path and settlement asset or execution pool for counter-trades.</FieldHelp>
          </div>
          <div className="font-mono">
            {task.config.sendMode ?? "global"}
            {task.chain !== "solana"
              ? ` / ${settlementLabel}`
              : ` / ${task.config.solanaSettlement === "sol" ? "SOL" : "pool quote"}`}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 border-t border-border px-4 py-4 sm:px-6 lg:grid-cols-2">
        <div>
          <div className="text-[11px] text-muted uppercase tracking-nav mb-2 flex items-center gap-1">
            Skipped (by reason)
            <FieldHelp>Every evaluated swap that did not create a counter-trade. The reason shows which filter blocked it, such as below-min-trigger-usd or exposure-limit.</FieldHelp>
          </div>
          {stats ? (
            <ul className="font-mono text-[11px] space-y-0.5">
              {Object.entries(stats.triggers.skipped).map(([reason, count]) => (
                <li key={reason}>
                  <span className="text-muted">{reason}</span> <span>{count}</span>
                </li>
              ))}
              {Object.keys(stats.triggers.skipped).length === 0 && (
                <li className="text-muted">none</li>
              )}
            </ul>
          ) : (
            <div className="font-mono text-[11px] text-muted">—</div>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-[11px] text-muted uppercase tracking-nav flex items-center gap-1">
              Wallets ({walletsDisplay.length})
              <FieldHelp>Wallets available to this task. Refresh reads their current native and target-token balances without starting or stopping the task.</FieldHelp>
            </div>
            <button
              type="button"
              onClick={() => void refreshBalances()}
              disabled={loadingBalances || task.walletPubkeys.length === 0}
              className="btn btn-ghost px-2 py-1 disabled:opacity-50"
              title="Refresh saved wallet balances without starting the task"
              aria-label="Refresh wallet balances"
            >
              <RefreshCw size={13} className={loadingBalances ? "animate-spin" : ""} />
              <span>{loadingBalances ? "Loading" : "Refresh"}</span>
            </button>
          </div>
          {balanceError && <div className="font-mono text-[11px] text-err mb-2">{balanceError}</div>}
          {walletsDisplay.length === 0 ? (
            <div className="font-mono text-[11px] text-muted">no wallets configured</div>
          ) : (
            <ul className="font-mono text-[11px] space-y-0.5">
              {walletsDisplay.map((w) =>
                w.live ? (
                  <li key={w.label}>
                    <span>{w.label}</span>{" "}
                    <span className="text-muted">
                      {w.pubkey.slice(0, 6)}…{w.pubkey.slice(-4)}
                    </span>{" "}
                    <span>{w.solSol.toFixed(4)} {nativeUnit}</span>{" "}
                    <span
                      className="text-muted"
                      title={`raw: ${w.tokens}${w.tokenUsdValue === null ? "" : ` · ≈$${fmtNum(w.tokenUsdValue, 4)}`}`}
                    >
                      {formatTokenAmount(w.tokens, w.tokenDecimals)} tok
                    </span>
                    {w.quotes && w.quotes.length > 0 && (
                      <>
                        {w.quotes.map((q) => (
                          <span key={q.mint} className="text-muted ml-2">
                            {" "}
                            |{" "}
                            <span title={q.mint}>
                              {formatTokenAmount(q.raw, q.decimals)}{" "}
                              {q.symbol ?? `${q.mint.slice(0, 4)}…${q.mint.slice(-4)}`}
                            </span>
                          </span>
                        ))}
                      </>
                    )}
                  </li>
                ) : (
                  <li key={w.label}>
                    <span>{w.label}</span>{" "}
                    <span className="text-muted">
                      {w.pubkey.slice(0, 6)}…{w.pubkey.slice(-4)}
                    </span>{" "}
                    {(() => {
                      const balance = manualBalanceByPubkey.get(w.pubkey.toLowerCase());
                      if (!balance) {
                        return <span className="text-muted">— refresh to see balance</span>;
                      }
                      return (
                        <>
                          <span>
                            {balance.nativeBalance === null
                              ? "unavailable"
                              : `${balance.nativeBalance.toFixed(4)} ${nativeUnit}`}
                          </span>
                          {balance.tokenBalance && (
                            <span
                              className="text-muted ml-2"
                              title={`raw: ${balance.tokenBalance.amount}`}
                            >
                              {formatTokenAmount(
                                balance.tokenBalance.amount,
                                balance.tokenBalance.decimals,
                              )} tok
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </li>
                ),
              )}
            </ul>
          )}
        </div>
      </div>

      <ExecutionHistory task={task} liveExecutions={executions} />
    </div>
  );
}
